import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ImagePlaceholder } from "./SiteShell";
import gazelleAsset from "@/assets/gazelle-2017.webp.asset.json";
import aaaAsset from "@/assets/kreditvaerdighed-aaa.webp.asset.json";
import teknisAsset from "@/assets/teknis.webp.asset.json";

export type HeroVariant =
  | "default"   // split with full trust-badges sidebar (homepage)
  | "split"     // text left, image right, no trust badges
  | "centered"  // centered text, image below, decorative grid
  | "compact"   // text-only, narrow, accent stripe
  | "stats"     // text + 4 stat cards
  | "media";    // large image background top, text card below

export function PageHero({
  eyebrow,
  title,
  intro,
  ctaTo = "/tilbud",
  ctaLabel = "Indhent tilbud",
  hideCta = false,
  imageLabel,
  imageSrc,
  videoUrl,
  variant = "default",
  stats,
  accentColor,
  coverage,
}: {
  eyebrow?: string;
  title: string;
  intro: string;
  ctaTo?: string;
  ctaLabel?: string;
  hideCta?: boolean;
  imageLabel?: string;
  imageSrc?: string;
  videoUrl?: string;
  variant?: HeroVariant;
  stats?: { value: string; label: string }[];
  accentColor?: string;
  coverage?: ReactNode;
}) {
  if (variant === "centered") return <HeroCentered {...{ eyebrow, title, intro, ctaTo, ctaLabel, hideCta, imageLabel, imageSrc, accentColor }} />;
  if (variant === "compact") return <HeroCompact {...{ eyebrow, title, intro, ctaTo, ctaLabel, hideCta, accentColor }} />;
  if (variant === "stats") return <HeroStats {...{ eyebrow, title, intro, ctaTo, ctaLabel, hideCta, stats, imageLabel, imageSrc }} />;
  if (variant === "media") return <HeroMedia {...{ eyebrow, title, intro, ctaTo, ctaLabel, hideCta, imageLabel, imageSrc, videoUrl }} />;
  if (variant === "split") return <HeroSplit {...{ eyebrow, title, intro, ctaTo, ctaLabel, hideCta, imageLabel, imageSrc, videoUrl, accentColor }} />;
  return <HeroDefault {...{ eyebrow, title, intro, ctaTo, ctaLabel, hideCta, imageLabel, imageSrc, videoUrl, coverage }} />;
}


function HeroBadges() {
  return (
    <>
      <div className="mt-8 flex items-center gap-4 rounded-xl border border-border bg-card/60 p-4">
        <img src={gazelleAsset.url} alt="Børsen Gazelle 2017" width={140} height={46} className="h-10 w-auto shrink-0" loading="eager" />
        <p className="text-sm text-[var(--ink)]/80">
          <span className="font-semibold text-[var(--ink)]">Gazelle-virksomhed 2017</span>, kåret af Børsen blandt Danmarks hurtigst voksende virksomheder.
        </p>
      </div>
      <div className="mt-3 flex items-center gap-4 rounded-xl border border-border bg-card/60 p-4">
        <img src={aaaAsset.url} alt="AAA kreditværdighed" width={100} height={40} className="h-10 w-auto shrink-0" loading="eager" />
        <p className="text-sm text-[var(--ink)]/80">
          <span className="font-semibold text-[var(--ink)]">Højeste kreditværdighed (AAA)</span>, solid og økonomisk stabil samarbejdspartner.
        </p>
      </div>
      <div className="mt-3 flex items-center gap-4 rounded-xl border border-border bg-card/60 p-4">
        <img src={teknisAsset.url} alt="Medlem af TEKNIQ" width={160} height={50} className="h-10 w-auto shrink-0" loading="eager" />
        <p className="text-sm text-[var(--ink)]/80">
          <span className="font-semibold text-[var(--ink)]">Medlem af TEKNIQ</span>, Garanti dækker op til 150.000 kr. ved fejl og mangler.
        </p>
      </div>
    </>
  );
}

function CTAButtons({ ctaTo, ctaLabel }: { ctaTo: string; ctaLabel: string }) {
  return (
    <div className="mt-8 flex flex-wrap gap-3">
      <Link to={ctaTo} className="inline-flex h-12 items-center justify-center rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-soft)] transition hover:brightness-95">
        {ctaLabel}
      </Link>
      <a href="tel:+4528700028" className="inline-flex h-12 items-center justify-center rounded-md border border-border bg-background px-6 text-sm font-semibold text-[var(--ink)] hover:bg-secondary">
        Ring +45 28 7000 28
      </a>
    </div>
  );
}

function Eyebrow({ children, color }: { children: ReactNode; color?: string }) {
  return (
    <span
      className="mb-4 inline-flex w-fit items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[var(--ink)]"
      style={{ background: color ?? "var(--accent)" }}
    >
      {children}
    </span>
  );
}

function HeroDefault({ eyebrow, title, intro, ctaTo = "/tilbud", ctaLabel = "Indhent tilbud", imageLabel, imageSrc, videoUrl, coverage }: any) {
  return (
    <section className="relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 opacity-60" style={{ background: "radial-gradient(60% 50% at 80% 0%, color-mix(in oklab, var(--brand) 22%, transparent), transparent 60%)" }} />
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
        <div className="grid gap-10 md:grid-cols-2">
          <div className="flex flex-col justify-center">
            {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
            <h1 className="text-4xl leading-tight md:text-5xl lg:text-6xl">{title}</h1>
            <p className="mt-5 max-w-xl text-lg text-muted-foreground">{intro}</p>
            <CTAButtons ctaTo={ctaTo} ctaLabel={ctaLabel} />
            <ul className="mt-8 grid grid-cols-2 gap-3 text-sm text-[var(--ink)]/80 sm:grid-cols-3">
              {["Autoriseret", "Hurtig udrykning", "Faste priser"].map((b) => (
                <li key={b} className="flex items-center gap-2">
                  <span aria-hidden className="grid h-5 w-5 place-items-center rounded-full bg-primary text-primary-foreground">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12l4 4 10-10" /></svg>
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            {videoUrl ? (
              <video autoPlay muted loop playsInline className="w-full rounded-2xl object-cover aspect-[4/3]" aria-label={imageLabel ?? "Hero-video"}>
                <source src={videoUrl} type="video/mp4" />
              </video>
            ) : (
              <ImagePlaceholder label={imageLabel ?? "Hero-billede kommer her"} src={imageSrc} ratio="aspect-[4/3]" />
            )}
          </div>
        </div>
        <div className="mt-10 grid gap-10 md:grid-cols-2">
          <HeroBadges />
          {coverage}
        </div>
      </div>
    </section>
  );
}

function HeroSplit({ eyebrow, title, intro, ctaTo = "/tilbud", ctaLabel = "Indhent tilbud", hideCta, imageLabel, imageSrc, videoUrl, accentColor }: any) {
  return (
    <section className="relative overflow-hidden border-b border-border">
      <div aria-hidden className="absolute inset-y-0 right-0 -z-10 w-1/2" style={{ background: "color-mix(in oklab, var(--brand) 6%, var(--background))" }} />
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 sm:px-6 md:grid-cols-[1.1fr_1fr] md:py-20 lg:px-8">
        <div>
          {eyebrow && <Eyebrow color={accentColor}>{eyebrow}</Eyebrow>}
          <h1 className="text-4xl leading-tight md:text-5xl lg:text-[3.5rem]">{title}</h1>
          <p className="mt-5 max-w-xl text-lg text-muted-foreground">{intro}</p>
          {!hideCta && <CTAButtons ctaTo={ctaTo} ctaLabel={ctaLabel} />}
        </div>
        <div className="relative">
          <div aria-hidden className="absolute -inset-4 -z-10 rounded-3xl" style={{ background: "var(--gradient-brand)", opacity: 0.15 }} />
          {videoUrl ? (
            <video autoPlay muted loop playsInline className="w-full rounded-2xl object-cover aspect-square" aria-label={imageLabel ?? "Hero-video"}>
              <source src={videoUrl} type="video/mp4" />
            </video>
          ) : (
            <ImagePlaceholder label={imageLabel ?? "Hero-billede"} src={imageSrc} ratio="aspect-square" />
          )}
        </div>
      </div>
    </section>
  );
}

function HeroCentered({ eyebrow, title, intro, ctaTo = "/tilbud", ctaLabel = "Indhent tilbud", imageLabel, imageSrc, accentColor }: any) {
  return (
    <section className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-10" style={{ backgroundImage: "linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)", backgroundSize: "32px 32px", maskImage: "radial-gradient(ellipse at center top, black 30%, transparent 75%)", opacity: 0.4 }} />
      <div className="mx-auto max-w-4xl px-4 pt-20 pb-10 text-center sm:px-6 md:pt-28 lg:px-8">
        {eyebrow && <span className="mx-auto mb-5 inline-flex items-center rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[var(--ink)]" style={accentColor ? { borderColor: accentColor } : undefined}>{eyebrow}</span>}
        <h1 className="text-4xl leading-tight md:text-5xl lg:text-6xl">{title}</h1>
        <p className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground">{intro}</p>
        <div className="mx-auto mt-8 flex flex-wrap justify-center gap-3">
          <Link to={ctaTo} className="inline-flex h-12 items-center justify-center rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-soft)] hover:brightness-95">{ctaLabel}</Link>
          <a href="tel:+4528700028" className="inline-flex h-12 items-center justify-center rounded-md border border-border bg-background px-6 text-sm font-semibold text-[var(--ink)] hover:bg-secondary">Ring +45 28 7000 28</a>
        </div>
      </div>
      <div className="mx-auto -mb-20 max-w-5xl px-4 sm:px-6 lg:px-8">
        <ImagePlaceholder label={imageLabel ?? "Billede"} src={imageSrc} ratio="aspect-[21/9]" />
      </div>
      <div className="h-20" />
    </section>
  );
}

function HeroCompact({ eyebrow, title, intro, ctaTo = "/tilbud", ctaLabel = "Indhent tilbud", hideCta, accentColor }: any) {
  return (
    <section className="relative">
      <div aria-hidden className="absolute left-0 top-0 h-full w-2" style={{ background: accentColor ?? "var(--brand)" }} />
      <div className="mx-auto max-w-5xl px-6 py-14 sm:px-8 md:py-20 lg:px-12">
        {eyebrow && <Eyebrow color={accentColor}>{eyebrow}</Eyebrow>}
        <h1 className="text-3xl leading-tight md:text-5xl">{title}</h1>
        <p className="mt-4 max-w-2xl text-lg text-muted-foreground">{intro}</p>
        {!hideCta && <CTAButtons ctaTo={ctaTo} ctaLabel={ctaLabel} />}
      </div>
    </section>
  );
}

function HeroStats({ eyebrow, title, intro, ctaTo = "/tilbud", ctaLabel = "Indhent tilbud", stats, imageLabel, imageSrc }: any) {
  const items = stats ?? [
    { value: "5.000+", label: "Tilfredse kunder" },
    { value: "<2 t", label: "Akut udrykning" },
    { value: "Garanti", label: "På arbejdet" },
    { value: "24/7", label: "Døgnvagt" },
  ];
  return (
    <section className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-10" style={{ background: "linear-gradient(180deg, color-mix(in oklab, var(--brand) 10%, var(--background)) 0%, var(--background) 100%)" }} />
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-20 lg:px-8">
        <div className="grid gap-10 md:grid-cols-[1.2fr_1fr] md:items-center">
          <div>
            {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
            <h1 className="text-4xl leading-tight md:text-5xl lg:text-6xl">{title}</h1>
            <p className="mt-5 max-w-xl text-lg text-muted-foreground">{intro}</p>
            <CTAButtons ctaTo={ctaTo} ctaLabel={ctaLabel} />
          </div>
          <ImagePlaceholder label={imageLabel ?? "Hero"} src={imageSrc} ratio="aspect-[4/3]" />
        </div>
        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
          {items.map((s: any) => (
            <div key={s.label} className="rounded-2xl border border-border bg-card p-5">
              <div className="text-3xl font-bold text-[var(--ink)]">{s.value}</div>
              <div className="mt-1 text-sm text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HeroMedia({ eyebrow, title, intro, ctaTo = "/tilbud", ctaLabel = "Indhent tilbud", imageLabel, imageSrc, videoUrl }: any) {
  return (
    <section className="relative">
      <div className="relative h-[42vh] min-h-[320px] w-full overflow-hidden md:h-[55vh]">
        {videoUrl ? (
          <video autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover">
            <source src={videoUrl} type="video/mp4" />
          </video>
        ) : (
          <div className="absolute inset-0">
            <ImagePlaceholder label={imageLabel ?? "Hero"} src={imageSrc} ratio="aspect-[21/9]" />
          </div>
        )}
        <div aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 30%, color-mix(in oklab, var(--ink) 70%, transparent) 100%)" }} />
      </div>
      <div className="mx-auto -mt-24 max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-border bg-card p-8 shadow-[var(--shadow-soft)] md:p-12">
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          <h1 className="text-3xl leading-tight md:text-5xl">{title}</h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">{intro}</p>
          <CTAButtons ctaTo={ctaTo} ctaLabel={ctaLabel} />
        </div>
      </div>
    </section>
  );
}

export function Section({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <section className={`mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-20 lg:px-8 ${className}`}>
      {children}
    </section>
  );
}

export function Prose({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`mx-auto max-w-3xl space-y-5 text-[var(--ink)]/85 leading-relaxed [&_h2]:text-3xl [&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:text-[var(--ink)] [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-[var(--ink)] [&_p]:text-[var(--ink)]/85 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_a]:text-[var(--brand)] [&_a:hover]:underline ${className}`}>
      {children}
    </div>
  );
}

export function FeatureGrid({ items }: { items: { title: string; body: string; icon?: ReactNode }[] }) {
  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {items.map((it) => (
        <article key={it.title} className="group rounded-2xl border border-border bg-card p-6 transition hover:border-primary/50 hover:shadow-[var(--shadow-soft)]">
          <div aria-hidden className="mb-4 grid h-11 w-11 place-items-center rounded-lg text-primary-foreground" style={{ background: "var(--gradient-brand)" }}>
            {it.icon ?? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
              </svg>
            )}
          </div>
          <h3 className="text-lg font-semibold text-[var(--ink)]">{it.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{it.body}</p>
        </article>
      ))}
    </div>
  );
}

export function FAQGrid({ items }: { items: { q: string; a: string }[] }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {items.map((f) => (
        <article key={f.q} className="rounded-2xl border border-border bg-card p-6">
          <h3 className="text-base font-semibold text-[var(--ink)]">{f.q}</h3>
          <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
        </article>
      ))}
    </div>
  );
}

export function CTA({
  title = "Skal vi løse din elektrikeropgave?",
  body = "Ring eller send en uforpligtende forespørgsel. Vi vender hurtigt tilbage med pris og tidspunkt.",
}: {
  title?: string;
  body?: string;
}) {
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-3xl p-8 text-white md:p-14" style={{ background: "linear-gradient(135deg, var(--ink), oklch(0.30 0.012 250))" }}>
        <div aria-hidden className="absolute -right-20 -top-20 h-72 w-72 rounded-full opacity-50 blur-3xl" style={{ background: "var(--brand)" }} />
        <div className="relative grid gap-6 md:grid-cols-[1.4fr_1fr] md:items-center">
          <div>
            <h2 className="text-2xl font-bold text-white md:text-3xl">{title}</h2>
            <p className="mt-3 max-w-xl text-white/80">{body}</p>
          </div>
          <div className="flex flex-wrap justify-start gap-3 md:justify-end">
            <Link to="/tilbud" className="inline-flex h-12 items-center justify-center rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground hover:brightness-95">Indhent tilbud</Link>
            <a href="tel:+4528700028" className="inline-flex h-12 items-center justify-center rounded-md border border-white/30 px-6 text-sm font-semibold text-white hover:bg-white/10">+45 28 7000 28</a>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Breadcrumbs({ items }: { items: { label: string; to?: string }[] }) {
  return (
    <nav aria-label="Brødkrumme" className="mx-auto max-w-7xl px-4 pt-6 text-sm text-muted-foreground sm:px-6 lg:px-8">
      <ol className="flex flex-wrap items-center gap-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-1.5">
            {it.to ? (
              <Link to={it.to} className="hover:text-[var(--ink)]">{it.label}</Link>
            ) : (
              <span className="text-[var(--ink)]">{it.label}</span>
            )}
            {i < items.length - 1 && <span aria-hidden>›</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}
